/**
 * データ型を考慮したソート機能
 */

import type { DataType, DataValue } from '../../../types/core.types';
import type { RowHeader, ColumnHeader, FieldDescriptor } from './types';
import { parseRowKey, parseColumnKey } from './keyGeneration';

/**
 * データ型に基づいて値を比較
 */
function compareByDataType(a: DataValue, b: DataValue, dataType: DataType): number {
  // NULL値の処理（NULLは常に最後）
  if (a === null || a === undefined) {
    if (b === null || b === undefined) return 0;
    return 1; // aがNULL、bが非NULL → aを後に
  }
  if (b === null || b === undefined) {
    return -1; // bがNULL、aが非NULL → aを前に
  }

  const typeStr = dataType.toLowerCase();

  // 数値型
  if (typeStr.includes('int') || typeStr.includes('float') || typeStr.includes('double')) {
    const numA = typeof a === 'number' ? a : parseFloat(String(a));
    const numB = typeof b === 'number' ? b : parseFloat(String(b));
    return numA - numB;
  }

  // 日付型
  if (typeStr.includes('date') || typeStr.includes('time')) {
    const dateA = new Date(String(a));
    const dateB = new Date(String(b));
    return dateA.getTime() - dateB.getTime();
  }

  // boolean型
  if (typeStr.includes('bool')) {
    const boolA = Boolean(a);
    const boolB = Boolean(b);
    return boolA === boolB ? 0 : boolA ? 1 : -1;
  }

  // 文字列型（デフォルト）
  return String(a).localeCompare(String(b));
}

/**
 * 行ヘッダーをソート
 *
 * @param headers 行ヘッダー配列
 * @param fields フィールド記述子配列
 * @returns ソート済み行ヘッダー配列
 */
export function sortRowHeaders(
  headers: RowHeader[],
  fields: FieldDescriptor[]
): RowHeader[] {
  return [...headers].sort((a, b) => {
    const valuesA = parseRowKey(a.key);
    const valuesB = parseRowKey(b.key);

    // 各フィールドレベルで比較
    for (let i = 0; i < Math.min(valuesA.length, valuesB.length); i++) {
      const field = fields[i];
      if (!field) continue;

      const sortOrder = field.sortOrder || 'asc';

      if (sortOrder === 'data') {
        // データ型に基づいてソート
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) return cmp;
      } else if (sortOrder === 'freq') {
        // 頻度順（現時点では未実装、通常のソートにフォールバック）
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) return cmp;
      } else {
        // 'asc' or 'desc'
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) {
          return sortOrder === 'asc' ? cmp : -cmp;
        }
      }
    }

    return 0;
  });
}

/**
 * 列ヘッダーをソート
 *
 * @param headers 列ヘッダー配列
 * @param fields フィールド記述子配列
 * @returns ソート済み列ヘッダー配列
 */
export function sortColumnHeaders(
  headers: ColumnHeader[],
  fields: FieldDescriptor[]
): ColumnHeader[] {
  return [...headers].sort((a, b) => {
    const valuesA = parseColumnKey(a.key);
    const valuesB = parseColumnKey(b.key);

    // 各フィールドレベルで比較
    for (let i = 0; i < Math.min(valuesA.length, valuesB.length); i++) {
      const field = fields[i];
      if (!field) continue;

      const sortOrder = field.sortOrder || 'asc';

      if (sortOrder === 'data') {
        // データ型に基づいてソート
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) return cmp;
      } else if (sortOrder === 'freq') {
        // 頻度順（現時点では未実装、通常のソートにフォールバック）
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) return cmp;
      } else {
        // 'asc' or 'desc'
        const cmp = compareByDataType(valuesA[i], valuesB[i], field.dataType);
        if (cmp !== 0) {
          return sortOrder === 'asc' ? cmp : -cmp;
        }
      }
    }

    return 0;
  });
}
